// client/src/components/DIVERS/RedirectQRC.js
// Point d'entrée du scan QR commerçant : garantit une session (compte existant restauré,
// sinon anonyme) puis redirige vers l'écran adapté au rôle.

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { firestore } from '../../firebase';
import { sanitize } from '../../utils/sanitize';
import { ensureUserSession } from '../../utils/ensureUserSession';
import ScreenState from '../shared/ScreenState';

const SLOW_HINT_MS = 6000;

function roleToPath(role, targetUid) {
  if (role === 'commercant' || role === 'commercantpending') {
    return `/merchantscanqrc/${targetUid}`;
  }
  return `/clientscanqrc/${targetUid}`;
}

const RedirectQRC = () => {
  const navigate = useNavigate();
  const { uid: rawUid } = useParams();
  const uid = rawUid ? sanitize(rawUid) : '';

  const [status, setStatus] = useState('loading'); // loading | slow | error
  const [attempt, setAttempt] = useState(0);

  const retry = useCallback(() => {
    setStatus('loading');
    setAttempt((n) => n + 1);
  }, []);

  useEffect(() => {
    let active = true;
    const slowTimer = setTimeout(() => {
      if (active) setStatus((s) => (s === 'loading' ? 'slow' : s));
    }, SLOW_HINT_MS);

    (async () => {
      const targetUid = encodeURIComponent(uid || '');
      let user;
      try {
        // Restaure la session existante (locale ou appareil) — anonyme seulement en dernier recours
        user = await ensureUserSession({ allowNewAnonymous: true, overrideLogoutGuard: true });
      } catch (error) {
        console.error('[RedirectQRC] session error', error);
        if (active) setStatus('error');
        return;
      }
      if (!active) return;

      const userDocRef = doc(firestore, 'users', user.uid);

      if (user.isAnonymous) {
        try {
          const snap = await getDoc(userDocRef);
          if (!snap.exists()) {
            await setDoc(userDocRef, { uid: user.uid, role: 'client', createdAt: serverTimestamp() }, { merge: true });
          }
        } catch (e) {
          console.warn('[RedirectQRC] user doc anonyme', e?.code || e);
        }
        if (active) navigate(`/clientscanqrc/${targetUid}`, { replace: true });
        return;
      }

      let role = 'client';
      try {
        const snap = await getDoc(userDocRef);
        if (snap.exists()) {
          role = snap.data()?.role || 'client';
        } else {
          await setDoc(userDocRef, { uid: user.uid, role: 'client', createdAt: serverTimestamp() }, { merge: true });
        }
      } catch (e) {
        console.warn('[RedirectQRC] lecture rôle', e?.code || e);
      }
      if (active) navigate(roleToPath(role, targetUid), { replace: true });
    })();

    return () => {
      active = false;
      clearTimeout(slowTimer);
    };
  }, [navigate, uid, attempt]);

  if (status === 'error') {
    return (
      <ScreenState
        variant="error"
        title="Connexion impossible"
        text="Vérifiez votre connexion internet puis réessayez. Vos points et votre compte sont conservés."
        actionLabel="Réessayer"
        onAction={retry}
      />
    );
  }

  return (
    <ScreenState
      title="Connexion à votre carte de fidélité"
      text={status === 'slow' ? 'Le réseau est un peu lent, on y est presque…' : 'Un instant…'}
    />
  );
};

export default RedirectQRC;
